import React from "react";

//I18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

// UI
import {
  Wizard,
  WizardHeader,
  WizardContent,
  WizardNav,
} from "@Shared/Components/Layouts/Wizard/Wizard";
import { Heading } from "@UI/Components/Heading/Heading";
import { Container } from "@UI/Components/Container/Container";
import { Text } from "@UI/Components/Text/Text";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { SolidInfoCircle } from "@UI/Components/Icons/SolidInfoCircle";
import { Flex } from "@UI/Components/Flex/Flex";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { BackLink } from "@Shared/Components/BackLink/BackLink";
import { ImproveAndGoal } from "./Common/ImproveAndGoal";
import { RemainingEffortNotice } from "./Common/RemainingEffortNotice";
import messages from "./messages";
import { TrackingsInfos } from "./Common/TrackingsInfos";
import { useRealisationRates } from "../hooks/useRealisationRate";
import { RealisationRateImpact } from "./RealisationRate/RealisationRateImpact";
import { ArrowRight } from "@UI/Components/Icons/ArrowRight";
import { useCurrentUser } from "@modules/Authentication/hooks/useCurrentUser";
import { Link } from "@UI/Components/Link/Link";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import { useFeatureFlag } from "@Libs/FeatureFlags/hooks/useFeatureFlag";

export const RealisationRate = () => {
  const { generateRoute } = useRoutes();
  const { isEnabled: isAdditionalSuperTalentMetricsEnabled } = useFeatureFlag(
    "additional_supertalent_metrics"
  );

  const { currentUser } = useCurrentUser({
    includes: ["serviceProvider"],
  });

  const { data: realisationRateMetrics, isLoading } = useRealisationRates(
    currentUser?.serviceProvider?.id
  );

  if (isLoading) {
    return <LoadingScreen />;
  }

  const onLeave = () => {
    history.back();
  };

  const handleDiscoverMoreURL = () => {
    return generateRoute("ACADEMY_REALISATION");
  };

  const handleOnClick = () => {
    return generateRoute("CANCELLATIONS");
  };

  return (
    <>
      <Wizard paddingTop={{ base: 5, md: 8 }}>
        <WizardNav>
          <BackLink onClick={onLeave} />
        </WizardNav>
        <WizardHeader marginBottom={{ base: 5, md: 3 }}>
          <Container
            px={{ base: 0 }}
            maxW={[
              "container.md",
              "container.sm",
              "container.md",
              "container.xl",
            ]}
          >
            <Heading textAlign="start" fontSize={{ base: "3xl", lg: "5xl" }}>
              <FormattedMessage {...messages.realisationRateTitle} />
            </Heading>
          </Container>
        </WizardHeader>
        <WizardContent>
          <Container
            px={{ base: 0 }}
            maxW={[
              "container.md",
              "container.sm",
              "container.md",
              "container.xl",
            ]}
          >
            <Box
              borderBottomWidth="1px"
              borderBottomColor="gray.300"
              pb={{ base: 5, md: 8 }}
            >
              <Box pb={{ base: 5, md: 8 }}>
                <Text as="span" fontSize={{ base: "md", md: "lg" }}>
                  <FormattedMessage {...messages.realisationRateInfos} />
                </Text>
              </Box>
              {isAdditionalSuperTalentMetricsEnabled &&
                realisationRateMetrics?.remainingEffort !== undefined && (
                  <RemainingEffortNotice
                    context="realisation_rate"
                    remainingEffort={realisationRateMetrics.remainingEffort}
                  />
                )}
              <TrackingsInfos metrics={realisationRateMetrics} />
              <Link as="a" href={handleOnClick()}>
                <Flex justifyContent="flex-end" cursor="pointer" mt={2}>
                  <Text
                    as="span"
                    fontSize={{ base: "sm", md: "md" }}
                    color="pink.500"
                    textDecoration="underline"
                  >
                    <FormattedMessage
                      {...messages.cancelation_summary}
                      values={{
                        RightIcon: () => (
                          <Icon as={ArrowRight} w={4} h={4} fontWeight={700} />
                        ),
                      }}
                    />
                  </Text>
                </Flex>
              </Link>
            </Box>

            <Box
              pb={{ base: 5, md: 8 }}
              pt={{ base: 5, md: 8 }}
              borderBottomWidth="1px"
              borderBottomColor="gray.300"
            >
              <RealisationRateImpact />
            </Box>

            <ImproveAndGoal context="realisation_rate" />

            <Link as="a" href={handleDiscoverMoreURL()}>
              <Flex
                cursor="pointer"
                alignItems="center"
                justifyContent="center"
                mt={{ base: 5, md: 8 }}
              >
                <Icon as={SolidInfoCircle} marginRight="4px" color="gray.500" />
                <Text
                  fontSize={{ base: "sm", md: "md" }}
                  color="gray.500"
                  textDecoration="underline"
                  mb={0}
                >
                  <FormattedMessage {...messages.discover_more} />
                </Text>
              </Flex>
            </Link>
          </Container>
        </WizardContent>
      </Wizard>
    </>
  );
};
